import { useId, type ReactNode } from "react";

/**
 * The one shell every Project clinical-configuration section renders inside
 * (#909).
 *
 * ## WHAT IT IS FOR
 *
 * The ratified Lovable reference for a Project section — [BG
 * sets](https://noproductcontent.lovable.app/projects/mod/config/bg-sets),
 * [Rules](https://noproductcontent.lovable.app/projects/mod/config/rules) — is
 * three things and nothing else: a right-aligned adoption action, a list of
 * compact rows each carrying its own menu, and the Project header/rail around
 * them. What this product rendered instead was those rows underneath a stack of
 * generic headings and paragraphs of implementation prose — a `Rules` card
 * title beneath a breadcrumb already reading `Rules`, a `What supplies Rules`
 * block, an `Adopt into Rules from the shared clinical library` block, each
 * with its own explanation of a projection contract. The rows were there; a
 * clinical author had to scroll past the essay to reach them.
 *
 * So this component owns the ARRANGEMENT, and nothing else. It fetches nothing,
 * decides nothing and renders no copy of its own. Every section keeps its own
 * body, its own action and its own evidence; what they stop keeping is a
 * private opinion about where those three things sit.
 *
 * ## THE HEADING IS ACCESSIBLE AND NOT VISIBLE
 *
 * `sectionLabel` is still an `h2` — a section with no heading is not navigable
 * by a screen-reader user, and this shell is the only element that can carry
 * one. It is `sr-only` because the visible name is already on the screen twice:
 * `PageHeader`'s breadcrumb leaf renders it with `aria-current="page"`, and
 * `SectionRail` marks the same label current. Lovable renders it in exactly
 * those two places and nowhere else. A third visible copy is the "generic
 * heading" this ticket names, not an accessibility affordance — and removing
 * the `h2` along with it would trade one defect for a worse one.
 *
 * ## THE FOUR SLOTS, AND WHY THEY ARE SLOTS
 *
 * Wave 1 delivers this shell; waves 2–5 deliver controls that stand inside it —
 * the BG profile selection (#858, #914), Closing conversation (#867), Speech
 * profile (#870) and the Summary policy toggle (#864). The acceptance criterion
 * is that each of those can arrive WITHOUT restructuring `ProjectLayoutRoute`
 * or `SectionSurface` again, so the insertion points are declared here, named,
 * and covered by `ProjectSectionPane.test.tsx`:
 *
 *  * `primaryAction` — the top-right action. Lovable's `+ Adopt from library`,
 *    and the place a second section-level action would join it.
 *  * `controls` — section-scoped controls ABOVE the rows: the typed settings
 *    the later leaves own. Above rather than below because they configure what
 *    the rows are read as, and a clinical author meets them before the list.
 *  * `children` — the section body. Compact rows, in every section that has
 *    them.
 *  * `details` — the technical evidence, collapsed. Revision ids, adopted-vs-
 *    head versions, pin-set cross-checks, engine and projection state: real,
 *    kept, addressable, and not in front of a clinical author who is adopting a
 *    BG domain. Nothing is deleted to reach the compact layout — an amendment
 *    that hid a refusal or a failed read would be the audit finding this
 *    surface exists to fix.
 *
 * A slot left out renders nothing at all. That is the same discipline the
 * adoption block applies to its own optional props: absent is a state, and an
 * empty container in place of an absent one is a layout claiming a thing exists.
 */
export function ProjectSectionPane({
  sectionLabel,
  primaryAction,
  controls,
  children,
  details,
  detailsLabel = "Details",
}: {
  /** The section's own name, from the rail. The accessible heading. */
  sectionLabel: string;
  /**
   * The top-right action, or absent where the section offers none.
   *
   * Lovable's `+ Adopt from library`. Rendered whatever the body below is, so
   * that a section holding no rows still offers it — the empty-section case the
   * ticket asks to keep discoverable.
   */
  primaryAction?: ReactNode;
  /**
   * Section-scoped controls above the rows, or absent.
   *
   * The stable insertion point for the typed controls waves 2–5 add. Also
   * where a section puts a notice its author must read before the list.
   */
  controls?: ReactNode;
  /** The section body: its compact rows. */
  children?: ReactNode;
  /**
   * The technical evidence, collapsed behind a disclosure, or absent.
   *
   * Never a summary of the body — the body is what a clinical author acts on.
   * This is what a support engineer needs and an author does not: exact
   * versions, identities, pin-set disagreements, which reads failed.
   */
  details?: ReactNode;
  /** The disclosure's own label. `Details`, matching the library screens. */
  detailsLabel?: string;
}) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      // Bounds the pane for a test that must scope an assertion to ONE
      // section — the `data-slot` convention `Card` documents, and the hop
      // those assertions used while every section was wrapped in a `Card`.
      data-slot="section-pane"
      className="space-y-3"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={headingId} className="sr-only">
          {sectionLabel}
        </h2>
        {primaryAction ? (
          // `ml-auto` rather than `justify-between`: the heading takes no
          // layout space (it is `sr-only`), so a space-between row would pin
          // the action to the LEFT edge on the sections that have no other
          // header content.
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {primaryAction}
          </div>
        ) : null}
      </div>

      {/*
        `data-slot` for the reason `Card` documents it: a test has to be able to
        assert that this container is ABSENT when a section has no controls,
        and an empty wrapper still takes a margin from the section's own
        `space-y-3` and opens a blank gap above the rows (Codex, #909 round 2).
        Whether it renders is the CALLER's decision — a caller passing a
        fragment that resolves to nothing gets an empty container, which is why
        `SectionAdoptionPanel` asks first.
      */}
      {controls ? (
        <div data-slot="section-controls" className="space-y-3">
          {controls}
        </div>
      ) : null}

      {children}

      {details ? (
        <details className="rounded-md border border-border bg-card">
          <summary
            // NAMED with the section, for the reason every ellipsis menu on
            // these rows is: a control listing of a Project surface that
            // mounts two panes would otherwise hold two controls called
            // "Details". The visible text stays the bare word, which WCAG
            // 2.5.3 requires to be contained in the accessible name.
            aria-label={`${detailsLabel} for ${sectionLabel}`}
            className="cursor-pointer px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {detailsLabel}
          </summary>
          <div className="border-t border-border px-4 py-3">{details}</div>
        </details>
      ) : null}
    </section>
  );
}
